/**
 * PSP/CRE Tool v2.0 - MTF Default Offsets from Parent HQ
 * 
 * Purpose: Define default positioning offsets for Medical Treatment Facilities
 * relative to their parent unit's HQ position with Ukraine-validated recommendations.
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: December 21, 2024
 * Version: 2.0 - Enhanced with icons.js integration and Ukraine 2022-2024 data
 * 
 * References:
 * - NATO STANAG 2542 (AJMedP-1) Allied Joint Medical Planning Doctrine
 * - US Army ATP 4-02.55 Army Health System Support Planning
 * - Romanian Army medical doctrine (2025)
 * - Ukraine 2022-2024 medical facility positioning data
 * 
 * Key Enhancements v2.0:
 * - All icons imported from icons.js
 * - Ukraine 2022-2024 validated offsets by threat level
 * - Security considerations for MTF positioning
 * - Drone/artillery threat distance calculations
 * 
 * Positioning Principle:
 * MTF Position = Parent HQ Position + Role-specific Offset
 * Example: Battalion HQ at 5km → Role 1 BAS at 5km + 3km = 8km from FEBA
 * 
 * Usage:
 * import { 
 *   MTF_DEFAULT_OFFSETS,
 *   calculateMTFPosition,
 *   getUkraineRecommendedOffset
 * } from '../constants/mtfDefaultOffsets';
 */

import { FACILITY_ICONS, STATUS_ICONS } from '../utils/icons';

// ============================================================================
// MTF OFFSET CONSTANTS BY ROLE
// ============================================================================

/**
 * Default offsets (in km) for MTF positioning behind parent HQ
 */
export const MTF_DEFAULT_OFFSETS = {
  ROLE1: {
    role: 'ROLE1',
    label: 'Role 1 (Battalion Aid Station)',
    shortLabel: 'BAS',
    icon: FACILITY_ICONS.ROLE1,
    echelon: 'BATTALION',
    
    // Offset from parent HQ
    default_offset_km: 3,
    min_offset_km: 2,
    max_offset_km: 5,
    
    // Ukraine-specific recommendations
    ukraine_offsets: {
      permissive: { default: 4, min: 3, max: 6 },
      contested: { default: 2, min: 1, max: 4 },
      denied: { default: 1, min: 0.5, max: 2 }
    },
    
    // Position from FEBA (typical)
    typical_feba_distance_km: { min: 3, max: 8, typical: 5 },
    
    // Security requirements
    security: {
      minimum_from_direct_fire: 0.5,
      recommended_from_artillery: 2,
      drone_threat_consideration: true,
      concealment_required: true
    },
    
    // Capabilities at this role
    capabilities: [
      'Triage and initial stabilization',
      'Tourniquet application/check',
      'Airway management',
      'IV access and fluid resuscitation',
      'Pain management',
      'Wound packing and dressings',
      'Hypothermia prevention'
    ],
    
    rationale: 'BAS must be close to maneuver battalions for rapid casualty collection. ' +
      'Positioned just behind FEBA within small arms range in some scenarios.',
    
    ukraine_lessons: [
      'Role 1 often within 2-3km of contact in active sectors',
      'Underground positions (basements) essential for protection',
      'Mobile Role 1 in vehicles for flexibility',
      'Drone threat requires camouflage and dispersion'
    ]
  },
  
  ROLE2_BASIC: {
    role: 'ROLE2_BASIC',
    label: 'Role 2 Basic (Brigade Medical Company)',
    shortLabel: 'BMC',
    icon: FACILITY_ICONS.ROLE2,
    echelon: 'BRIGADE',
    
    default_offset_km: 6,
    min_offset_km: 5,
    max_offset_km: 10,
    
    ukraine_offsets: {
      permissive: { default: 8, min: 6, max: 12 },
      contested: { default: 5, min: 4, max: 8 },
      denied: { default: 3, min: 2, max: 5 }
    },
    
    typical_feba_distance_km: { min: 10, max: 25, typical: 15 },
    
    security: {
      minimum_from_direct_fire: 5,
      recommended_from_artillery: 15,
      drone_threat_consideration: true,
      concealment_required: true,
      hardened_structure_preferred: true
    },
    
    capabilities: [
      'Damage Control Surgery (limited)',
      'Blood product transfusion',
      'Advanced airway management',
      'Surgical hemorrhage control',
      'Stabilization for evacuation',
      'Walking Blood Bank capability'
    ],
    
    rationale: 'FST provides damage control surgery. Must be forward enough to meet ' +
      'golden hour, far enough back for operational security and setup time.',
    
    ukraine_lessons: [
      'Closer positioning (5-8km from FEBA) improved golden hour',
      'Hardened structures (schools, factories) essential',
      'Frequent displacement due to artillery targeting',
      'Multiple locations required for survivability'
    ]
  },
  
  ROLE2_ENHANCED: {
    role: 'ROLE2_ENHANCED',
    label: 'Role 2 Enhanced (Forward Surgical Team)',
    shortLabel: 'FST-E',
    icon: FACILITY_ICONS.ROLE2,
    echelon: 'DIVISION',
    
    default_offset_km: 10,
    min_offset_km: 8,
    max_offset_km: 15,
    
    ukraine_offsets: {
      permissive: { default: 12, min: 10, max: 18 },
      contested: { default: 8, min: 6, max: 12 },
      denied: { default: 5, min: 4, max: 8 }
    },
    
    typical_feba_distance_km: { min: 20, max: 45, typical: 30 },
    
    security: {
      minimum_from_direct_fire: 15,
      recommended_from_artillery: 25,
      drone_threat_consideration: true,
      concealment_required: true,
      hardened_structure_required: true,
      generator_protection: true
    },
    
    capabilities: [
      'Extended surgical capability',
      'Multiple operating rooms',
      'Blood bank with storage',
      'ICU-level post-op care',
      'X-ray and basic imaging',
      'Laboratory services'
    ],
    
    rationale: 'Enhanced FST has more capability, requires more space and protection. ' +
      'Positioned in division support area.',
    
    ukraine_lessons: [
      'Division-level facilities hit by missiles/Shahed drones',
      'Dispersion into multiple smaller facilities preferred',
      'Underground facilities dramatically improved survival',
      'Counter-battery radar placement near medical reduced attacks'
    ]
  },
  
  ROLE3: {
    role: 'ROLE3',
    label: 'Role 3 (Combat Support Hospital)',
    shortLabel: 'CSH',
    icon: FACILITY_ICONS.ROLE3,
    echelon: 'CORPS',
    
    default_offset_km: 20,
    min_offset_km: 15,
    max_offset_km: 30,
    
    ukraine_offsets: {
      permissive: { default: 25, min: 20, max: 35 },
      contested: { default: 15, min: 12, max: 22 },
      denied: { default: 10, min: 8, max: 15 }
    },
    
    typical_feba_distance_km: { min: 60, max: 120, typical: 80 },
    
    security: {
      minimum_from_direct_fire: 50,
      recommended_from_artillery: 60,
      missile_threat_consideration: true,
      air_defense_coverage_required: true,
      hardened_structure_required: true,
      backup_power_essential: true
    },
    
    capabilities: [
      'Full surgical capability',
      'Definitive care for most injuries',
      'Blood bank with production',
      'Extended ICU capability',
      'Full imaging (CT, X-ray)',
      'Laboratory and pharmacy',
      'Burn treatment',
      'Specialty surgical teams'
    ],
    
    rationale: 'CSH provides definitive care. Positioned in corps rear area, ' +
      'protected from direct fire and most artillery. Requires airfield access.',
    
    ukraine_lessons: [
      'Multiple Role 3 hit by cruise missiles/ballistic missiles',
      '60-100km from FEBA minimum for survivability',
      'Air defense umbrella essential',
      'Underground/reinforced facilities only',
      'Dispersion into civilian hospital integration'
    ]
  },
  
  ROLE4: {
    role: 'ROLE4',
    label: 'Role 4 (Strategic/National Hospital)',
    shortLabel: 'National',
    icon: FACILITY_ICONS.ROLE4,
    echelon: 'STRATEGIC',
    
    default_offset_km: 0,  // Fixed location
    min_offset_km: 0,
    max_offset_km: 0,
    
    typical_feba_distance_km: { min: 200, max: 1000, typical: 400 },
    
    security: {
      national_air_defense: true,
      critical_infrastructure_protection: true,
      backup_facilities_required: true
    },
    
    capabilities: [
      'All surgical specialties',
      'Rehabilitation',
      'Reconstructive surgery',
      'Prosthetics',
      'Psychological care',
      'Long-term care'
    ],
    
    rationale: 'National military or civilian hospitals in strategic rear. ' +
      'Definitive and rehabilitative care.',
    
    ukraine_lessons: [
      'Strategic evacuation to Poland, Germany essential',
      'National hospitals overwhelmed - international support critical',
      'Specialist care (burns, neuro) required evacuation abroad',
      'Rehabilitation centers in Western Ukraine/abroad'
    ]
  }
};

// ============================================================================
// THREAT-BASED OFFSET ADJUSTMENTS
// ============================================================================

/**
 * Offset adjustments based on threat level
 */
export const THREAT_OFFSET_ADJUSTMENTS = {
  MINIMAL: {
    multiplier: 1.2,
    description: 'Low threat - can position further back for comfort',
    icon: STATUS_ICONS.GREEN
  },
  LOW: {
    multiplier: 1.0,
    description: 'Standard positioning per doctrine',
    icon: STATUS_ICONS.GREEN
  },
  MODERATE: {
    multiplier: 0.85,
    description: 'Closer positioning to reduce evacuation time',
    icon: STATUS_ICONS.YELLOW
  },
  HIGH: {
    multiplier: 0.70,
    description: 'Much closer positioning, accept risk for golden hour',
    icon: STATUS_ICONS.YELLOW_WARNING
  },
  EXTREME: {
    multiplier: 0.50,
    description: 'Minimum safe distance, forward positioning essential',
    icon: STATUS_ICONS.RED
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get default offset for a specific MTF role
 * @param {string} role - MTF role (ROLE1, ROLE2_BASIC, ROLE2_ENHANCED, ROLE3)
 * @returns {number} Default offset in kilometers
 */
export function getDefaultOffset(role) {
  const config = MTF_DEFAULT_OFFSETS[role];
  return config ? config.default_offset_km : 3;
}

/**
 * Get MTF offset configuration with ranges
 * @param {string} role - MTF role
 * @returns {Object} Complete offset configuration
 */
export function getMTFOffsetConfig(role) {
  return MTF_DEFAULT_OFFSETS[role] || MTF_DEFAULT_OFFSETS.ROLE1;
}

/**
 * Calculate MTF position from parent HQ position
 * @param {number} hqPositionKm - Parent HQ position (km from FEBA)
 * @param {string} role - MTF role
 * @param {string} paradigm - Evacuation paradigm (optional)
 * @returns {number} MTF position (km from FEBA)
 */
export function calculateMTFPosition(hqPositionKm, role, paradigm = null) {
  const config = MTF_DEFAULT_OFFSETS[role];
  if (!config) return hqPositionKm + 3;
  
  let offset = config.default_offset_km;
  
  // Use paradigm-specific offset if available
  if (paradigm && config.ukraine_offsets) {
    const paradigmLower = paradigm.toLowerCase();
    if (config.ukraine_offsets[paradigmLower]) {
      offset = config.ukraine_offsets[paradigmLower].default;
    }
  }
  
  return hqPositionKm + offset;
}

// ----------------------------------------------------------------------------
// 2026-10-13 (Tab 2 follow-up, Dr. Tatar): offset from the parent HQ by evacuation paradigm.
// An HQ is a target (heat signature, antennas), so under threat the facility sits FURTHER from it, not closer: dispersion.
// The older `ukraine_offsets` tables (closer as the threat rises, golden-hour logic) and THREAT_OFFSET_ADJUSTMENTS (x0.5 at
// EXTREME) are kept unchanged, behind the switch MTF_OFFSET_DISPERSION_BY_PARADIGM = false.
// Status: EDUCATED GUESS. PERMISSIVE = the doctrinal default; the Role 3 DENIED value follows the Ukraine lesson in
// MTF_OFFSET_EDUCATIONAL ("Role 3 hit by missiles, 60 km+"); the other values are steps in between. No source for the exact km.
// ----------------------------------------------------------------------------
export const MTF_OFFSET_DISPERSION_BY_PARADIGM = true;

export const MTF_PARADIGM_OFFSETS_KM = {
  ROLE1:         { PERMISSIVE: 3,  CONTESTED: 4,  DENIED: 6 },
  ROLE2_BASIC:   { PERMISSIVE: 6,  CONTESTED: 8,  DENIED: 12 },
  ROLE2_ENHANCED:{ PERMISSIVE: 10, CONTESTED: 14, DENIED: 20 },
  ROLE3:         { PERMISSIVE: 20, CONTESTED: 35, DENIED: 60 }
};
export const MTF_PARADIGM_OFFSETS_STATUS = { status: 'EDUCATED_GUESS', basis: 'Dispersion logic; Role 3 DENIED from the Ukraine lesson (60 km+ minimum); other values interpolated.' };

/** Offset (km) of a role from its parent HQ for a paradigm; the doctrinal default when the switch is off or the role has no row. */
export function paradigmOffsetKm(role, paradigm) {
  if (!MTF_OFFSET_DISPERSION_BY_PARADIGM) return getDefaultOffset(role);
  const row = MTF_PARADIGM_OFFSETS_KM[role];
  if (!row) return getDefaultOffset(role);
  if (!(paradigm in row)) {
    throw new Error(`paradigmOffsetKm: unknown evacuation paradigm '${paradigm}' (valid: ${Object.keys(row).join(', ')})`);
  }
  return row[paradigm];
}

/**
 * Facilities are built with the doctrinal default offset behind the HQ (ForceService, no paradigm at Tab 1). This moves each
 * facility of a known role to HQ + paradigm offset: hq = position - default offset. Facilities of other roles are returned as they are.
 */
export function applyParadigmToFacilityPositions(facilities, paradigm) {
  return facilities.map((f) => {
    if (!MTF_PARADIGM_OFFSETS_KM[f.type] || typeof f.position_feba_km !== 'number') return f;
    const hq = f.position_feba_km - getDefaultOffset(f.type);
    const offset = paradigmOffsetKm(f.type, paradigm);
    return { ...f, position_feba_km: hq + offset, mtf_offset_km: offset, mtf_offset_paradigm: paradigm };
  });
}

/**
 * Get Ukraine-recommended offset for threat level
 * @param {string} role - MTF role
 * @param {string} paradigm - PERMISSIVE, CONTESTED, or DENIED
 * @returns {Object} Recommended offset configuration
 */
export function getUkraineRecommendedOffset(role, paradigm = 'CONTESTED') {
  const config = MTF_DEFAULT_OFFSETS[role];
  if (!config || !config.ukraine_offsets) {
    return { default: getDefaultOffset(role), min: 1, max: 10 };
  }
  
  const paradigmLower = paradigm.toLowerCase();
  return config.ukraine_offsets[paradigmLower] || config.ukraine_offsets.contested;
}

/**
 * Validate MTF offset against standard ranges
 * @param {string} role - MTF role
 * @param {number} offset - Proposed offset (km)
 * @returns {Object} Validation result
 */
export function validateMTFOffset(role, offset) {
  const config = MTF_DEFAULT_OFFSETS[role];
  
  if (!config) {
    return {
      valid: false,
      error: 'Invalid MTF role specified',
      icon: STATUS_ICONS.RED,
      role: role
    };
  }
  
  if (offset < config.min_offset_km) {
    return {
      valid: false,
      severity: 'error',
      icon: STATUS_ICONS.RED,
      error: `Offset too small. Minimum: ${config.min_offset_km} km`,
      min: config.min_offset_km,
      max: config.max_offset_km,
      role: config.label
    };
  }
  
  if (offset > config.max_offset_km) {
    return {
      valid: false,
      severity: 'error',
      icon: STATUS_ICONS.RED,
      error: `Offset too large. Maximum: ${config.max_offset_km} km`,
      min: config.min_offset_km,
      max: config.max_offset_km,
      role: config.label
    };
  }
  
  return {
    valid: true,
    severity: 'success',
    icon: STATUS_ICONS.GREEN,
    role: config.label,
    offset: offset
  };
}

/**
 * Get all MTF roles as array (for UI dropdowns)
 * @returns {Array} Array of MTF role configurations
 */
export function getAllMTFRoles() {
  return Object.values(MTF_DEFAULT_OFFSETS).map(config => ({
    role: config.role,
    label: config.label,
    shortLabel: config.shortLabel,
    icon: config.icon,
    echelon: config.echelon,
    default_offset_km: config.default_offset_km
  }));
}

/**
 * Get MTF role by echelon
 * @param {string} echelon - Force echelon (BATTALION, BRIGADE, DIVISION, CORPS)
 * @returns {string} MTF role
 */
export function getMTFRoleByEchelon(echelon) {
  const mapping = {
    'BATTALION': 'ROLE1',
    'BRIGADE': 'ROLE2_BASIC',
    'DIVISION': 'ROLE2_ENHANCED',
    'CORPS': 'ROLE3',
    'STRATEGIC': 'ROLE4'
  };
  
  return mapping[echelon] || 'ROLE1';
}

/**
 * Get MTF type name by role
 * @param {string} role - MTF role
 * @returns {string} Facility type name
 */
export function getMTFTypeName(role) {
  const config = MTF_DEFAULT_OFFSETS[role];
  return config ? config.shortLabel : 'MTF';
}

/**
 * Get MTF full label by role
 * @param {string} role - MTF role
 * @returns {string} Full label
 */
export function getMTFLabel(role) {
  const config = MTF_DEFAULT_OFFSETS[role];
  return config ? config.label : role;
}

/**
 * Calculate adjusted offset based on threat
 * @param {string} role - MTF role
 * @param {string} threatLevel - Threat level
 * @returns {number} Adjusted offset in km
 */
export function calculateThreatAdjustedOffset(role, threatLevel) {
  const baseOffset = getDefaultOffset(role);
  const adjustment = THREAT_OFFSET_ADJUSTMENTS[threatLevel];
  
  if (!adjustment) return baseOffset;
  
  return Math.round(baseOffset * adjustment.multiplier * 10) / 10;
}

/**
 * Calculate all MTF positions for a hierarchical force
 * @param {Object} force - Force structure with unit HQ positions
 * @param {string} paradigm - Evacuation paradigm
 * @returns {Array} Array of MTF positions
 */
export function calculateAllMTFPositions(force, paradigm = 'CONTESTED') {
  const positions = [];
  
  const processUnit = (unit, role) => {
    if (unit.tactical_in_parent && unit.tactical_in_parent.hq_position_feba_km) {
      const mtfPosition = calculateMTFPosition(
        unit.tactical_in_parent.hq_position_feba_km,
        role,
        paradigm
      );
      
      const config = MTF_DEFAULT_OFFSETS[role];
      
      positions.push({
        unit_id: unit.id,
        unit_name: unit.name,
        unit_echelon: unit.type,
        hq_position_km: unit.tactical_in_parent.hq_position_feba_km,
        mtf_role: role,
        mtf_type: getMTFTypeName(role),
        mtf_label: getMTFLabel(role),
        mtf_icon: config?.icon,
        mtf_position_km: mtfPosition,
        offset_km: mtfPosition - unit.tactical_in_parent.hq_position_feba_km,
        paradigm: paradigm,
        capabilities: config?.capabilities || []
      });
    }
  };
  
  // Process force structure recursively
  if (force.type === 'CORPS' && force.divisions) {
    processUnit(force, 'ROLE3');
    force.divisions.forEach(division => {
      if (division.brigades) {
        processUnit(division, 'ROLE2_ENHANCED');
        division.brigades.forEach(brigade => {
          if (brigade.battalions) {
            processUnit(brigade, 'ROLE2_BASIC');
            brigade.battalions.forEach(battalion => {
              processUnit(battalion, 'ROLE1');
            });
          }
        });
      }
    });
  } else if (force.type === 'DIVISION' && force.brigades) {
    processUnit(force, 'ROLE2_ENHANCED');
    force.brigades.forEach(brigade => {
      if (brigade.battalions) {
        processUnit(brigade, 'ROLE2_BASIC');
        brigade.battalions.forEach(battalion => {
          processUnit(battalion, 'ROLE1');
        });
      }
    });
  } else if (force.type === 'BRIGADE' && force.battalions) {
    processUnit(force, 'ROLE2_BASIC');
    force.battalions.forEach(battalion => {
      processUnit(battalion, 'ROLE1');
    });
  } else if (force.type === 'BATTALION') {
    processUnit(force, 'ROLE1');
  }
  
  return positions;
}

/**
 * Get security requirements for MTF role
 * @param {string} role - MTF role
 * @returns {Object} Security requirements
 */
export function getSecurityRequirements(role) {
  const config = MTF_DEFAULT_OFFSETS[role];
  return config?.security || {
    minimum_from_direct_fire: 5,
    recommended_from_artillery: 15
  };
}

// ============================================================================
// EDUCATIONAL CONTENT
// ============================================================================

export const MTF_OFFSET_EDUCATIONAL = {
  key_principles: [
    'MTF Position = Parent HQ Position + Role-specific Offset',
    'Closer positioning improves golden hour but increases risk',
    'Higher echelon MTFs need more distance for security',
    'Threat level should adjust offset distances'
  ],
  
  ukraine_lessons: [
    'Role 1 often within 2-3km of contact - underground positions essential',
    'Role 2 closer than doctrine (5-8km) improved golden hour',
    'Role 3 hit by missiles - 60km+ minimum recommended',
    'Dispersion into multiple smaller facilities improved survival',
    'Counter-battery radar near medical reduced artillery attacks'
  ],
  
  planning_factors: [
    'Role 1: Close enough for rapid collection, protected from direct fire',
    'Role 2: Balance golden hour vs survivability',
    'Role 3: Outside artillery range, air defense coverage',
    'Role 4: Strategic rear, national infrastructure'
  ],
  
  common_errors: [
    'Using peacetime offsets in high-threat environment',
    'Not adjusting for threat level',
    'Ignoring drone/artillery threat radius',
    'Single location vs dispersed positioning'
  ]
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

// Named default export (fixes lint warning)
const mtfDefaultOffsetsConstants = {
  MTF_DEFAULT_OFFSETS,
  THREAT_OFFSET_ADJUSTMENTS,
  getDefaultOffset,
  getMTFOffsetConfig,
  calculateMTFPosition,
  getUkraineRecommendedOffset,
  validateMTFOffset,
  getAllMTFRoles,
  getMTFRoleByEchelon,
  getMTFTypeName,
  getMTFLabel,
  calculateThreatAdjustedOffset,
  calculateAllMTFPositions,
  getSecurityRequirements,
  MTF_OFFSET_EDUCATIONAL,
  MTF_PARADIGM_OFFSETS_KM,
  paradigmOffsetKm,
  applyParadigmToFacilityPositions
};

export default mtfDefaultOffsetsConstants;
